import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, TextField, Button, Stack, Switch,
  Select, MenuItem, FormControl, InputLabel, IconButton, Tabs, Tab,
  Avatar, CircularProgress, InputAdornment, Pagination, Tooltip, Chip,
  Divider, Alert, Card, CardContent,
} from '@mui/material';
import {
  Save as SaveIcon, UploadFile as UploadIcon, Delete as DeleteIcon,
  Image as ImageIcon, Description as DescIcon, Star as StarIcon,
  LocalOffer as PriceIcon, Inventory as StockIcon,
  Sync as SyncIcon, PhotoCamera as CameraIcon,
  WarningAmber as WarningIcon, CheckCircle as CheckCircleIcon,
  ContentCopy as CopyIcon, Check as CheckIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { getImageUrl } from '../config';
import { color, radius } from '../theme/tokens';
import {
  PageHeader, Panel, DataTable, Toolbar, StatusBadge,
  EmptyState, ConfirmDialog, FormDialog, useFeedback,
} from '../components/ui';

const MARKETING_FLAGS = [
  { key: 'is_featured',    label: 'Featured in Spotlight', description: 'Highlight this product in prominent featured homepage sections.' },
  { key: 'is_todays_deal', label: "Today's Special Deal",  description: 'Surface this product under top daily discount shelves.' },
  { key: 'is_best_seller', label: 'Best-Selling Phone',    description: 'Display this product on the "Best-selling phones" shelf.' },
  { key: 'is_new_release', label: 'New Arrival Release',   description: 'Showcase this product under newly arrived catalog launches.' },
];

const FLAG_BADGES = [
  { key: 'is_featured',    letter: 'F', label: 'Featured',     tone: 'gold' },
  { key: 'is_todays_deal', letter: 'D', label: "Today's Deal", tone: 'danger' },
  { key: 'is_best_seller', letter: 'B', label: 'Best Seller',  tone: 'primary' },
  { key: 'is_new_release', letter: 'N', label: 'New Release',  tone: 'success' },
];

const ProductEditDialog = ({ open, onClose, product, categories, onSaved }) => {
  const { success, error: showError } = useFeedback();
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({});
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    if (open && product) {
      setForm({
        name: product.name || '',
        erp_name: product.erp_name || '',
        brand: product.brand || '',
        category_id: product.category_id || '',
        mrp: product.mrp ?? 0,
        sale_price: product.sale_price ?? 0,
        cost_price: product.cost_price ?? 0,
        stock_qty: product.stock_qty ?? 0,
        sku: product.sku || '',
        item_code: product.item_code || '',
        description: product.description || '',
        short_desc: product.short_desc || '',
        is_active: product.is_active ?? 1,
        is_featured: product.is_featured ?? 0,
        is_todays_deal: product.is_todays_deal ?? 0,
        is_best_seller: product.is_best_seller ?? 0,
        is_new_release: product.is_new_release ?? 0,
      });
      loadImages(product.id);
      setError('');
      setTab(0); // Default to Photos & Packshots tab
    }
  }, [open, product]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadImages = async (id) => {
    try {
      const res = await api.get(`/api/v2/products/${id}/images`);
      setImages(res.data.data || []);
    } catch {
      setImages([]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        brand: form.brand,
        category_id: form.category_id || null,
        mrp: Number(form.mrp) || 0,
        sale_price: Number(form.sale_price) || 0,
        cost_price: Number(form.cost_price) || 0,
        short_desc: form.short_desc,
        description: form.description,
        is_active: form.is_active,
        is_featured: form.is_featured,
        is_todays_deal: form.is_todays_deal,
        is_best_seller: form.is_best_seller,
        is_new_release: form.is_new_release,
      };

      await api.put(`/api/v2/products/${product.id}`, payload);
      success('Product presentation updated successfully');
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImage = async (file) => {
    if (!file || !product?.id) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);

      // Upload directly via official ItemCode WebP pipeline
      const res = await api.post(`/api/v2/products/${product.id}/upload-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        success('Photo uploaded and auto-converted to WebP with thumbnails');
        await loadImages(product.id);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Photo upload failed');
      showError(err.response?.data?.message || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const setPrimary = async (imgId) => {
    try {
      await api.put(`/api/v2/products/${product.id}/images/${imgId}`, { is_primary: true });
      success('Primary hero photo updated');
      await loadImages(product.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set primary image');
    }
  };

  const deleteImage = async (imgId) => {
    try {
      await api.delete(`/api/v2/products/${product.id}/images/${imgId}`);
      success('Image removed');
      await loadImages(product.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove image');
    }
  };

  const copyItemCode = () => {
    if (form.item_code) {
      navigator.clipboard.writeText(form.item_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  // Convert short_desc lines to preview bullets
  const specBullets = (form.short_desc || '')
    .split('\n')
    .map(s => s.trim().replace(/^[-•*]\s*/, ''))
    .filter(s => s.length > 2);

  const actions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button onClick={onClose} disabled={saving} variant="outlined" color="inherit">
        Close
      </Button>
      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={handleSave}
        disabled={saving || !form.name || !form.sale_price}
        sx={{ bgcolor: color.primary[600], '&:hover': { bgcolor: color.primary[700] } }}
      >
        {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
      </Button>
    </Stack>
  );

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={form.name || 'Manage Product Presentation'}
      subtitle="FCA ERP Synced Item • Manage Customer Packshots, Specifications, and Merchandising"
      icon={<CameraIcon fontSize="small" />}
      maxWidth="md"
      fullWidth
      saving={saving}
      error={error}
      actions={actions}
    >
      {/* Header Info Banner */}
      <Box
        sx={{
          mb: 2.5,
          p: 1.5,
          borderRadius: `${radius.lg}px`,
          bgcolor: color.ink[50],
          border: `1px solid ${color.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={form.item_code ? `ERP Code: ${form.item_code}` : 'No ERP Code'}
            onDelete={form.item_code ? copyItemCode : undefined}
            deleteIcon={copiedCode ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
            sx={{ fontWeight: 600, fontFamily: 'monospace' }}
          />
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label="ERP Synced"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
        {form.erp_name && (
          <Typography sx={{ fontSize: '0.75rem', color: color.ink[500], fontStyle: 'italic' }}>
            ERP Name: {form.erp_name}
          </Typography>
        )}
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          label={`Photos (${images.length})`}
          icon={<ImageIcon fontSize="small" />}
          iconPosition="start"
          sx={{ fontWeight: 600 }}
        />
        <Tab
          label={`Specifications (${specBullets.length})`}
          icon={<DescIcon fontSize="small" />}
          iconPosition="start"
          sx={{ fontWeight: 600 }}
        />
        <Tab
          label="Basic Info & Price"
          icon={<PriceIcon fontSize="small" />}
          iconPosition="start"
          sx={{ fontWeight: 600 }}
        />
        <Tab
          label="Merchandising & Flags"
          icon={<StarIcon fontSize="small" />}
          iconPosition="start"
          sx={{ fontWeight: 600 }}
        />
      </Tabs>

      {/* TAB 0: PHOTOS & PACKSHOTS */}
      {tab === 0 && (
        <Box>
          <Box
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: `${radius.lg}px`,
              border: `2px dashed ${uploading ? color.primary[400] : color.border}`,
              bgcolor: uploading ? color.primary[50] : color.surface,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: color.primary[500], bgcolor: color.primary[50] }
            }}
            component="label"
          >
            <input
              type="file"
              accept="image/*"
              hidden
              disabled={uploading}
              onChange={e => {
                if (e.target.files?.[0]) handleUploadImage(e.target.files[0]);
              }}
            />
            {uploading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                <CircularProgress size={22} color="primary" />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: color.primary[700] }}>
                  Processing high-res WebP master and thumbnails...
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={0.75} alignItems="center">
                <UploadIcon sx={{ fontSize: 32, color: color.primary[600] }} />
                <Typography sx={{ fontSize: '0.925rem', fontWeight: 600, color: color.ink[900] }}>
                  Click or drag photo here to upload packshot
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: color.ink[500] }}>
                  Automatically formatted to WebP, Master, 600px & 200px thumb with official <code style={{ color: color.primary[700] }}>{form.item_code || 'ItemCode'}_X.webp</code> naming
                </Typography>
              </Stack>
            )}
          </Box>

          {images.length === 0 ? (
            <EmptyState
              icon={<ImageIcon sx={{ fontSize: 28, color: color.danger[500] }} />}
              title="No photos attached to this product"
              description="Upload an official packshot above to show this phone on the customer storefront. The first image uploaded becomes the primary hero image automatically."
            />
          ) : (
            <Grid container spacing={2}>
              {images.map((img, idx) => (
                <Grid item xs={12} sm={6} md={4} key={img.id || idx}>
                  <Box
                    sx={{
                      border: img.is_primary ? `2px solid ${color.primary[500]}` : `1px solid ${color.border}`,
                      borderRadius: `${radius.lg}px`,
                      overflow: 'hidden',
                      bgcolor: color.surface,
                      position: 'relative',
                      boxShadow: img.is_primary ? '0 4px 12px rgba(224, 0, 0, 0.12)' : 'none'
                    }}
                  >
                    <Box sx={{ position: 'relative', pt: '90%', bgcolor: '#ffffff' }}>
                      <img
                        src={getImageUrl(img.image_url)}
                        alt={img.alt_text || form.name}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '12px'
                        }}
                      />
                      {img.is_primary ? (
                        <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                          <Chip size="small" color="primary" label="Primary Packshot" sx={{ fontWeight: 600 }} />
                        </Box>
                      ) : (
                        <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                          <Chip size="small" variant="outlined" label={`Gallery #${idx + 1}`} sx={{ bgcolor: 'rgba(255,255,255,0.9)' }} />
                        </Box>
                      )}
                    </Box>

                    <Divider />

                    <Stack spacing={1} sx={{ p: 1.5 }}>
                      <Typography sx={{ fontSize: '0.725rem', fontFamily: 'monospace', color: color.ink[500], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {img.image_url?.split('/')?.pop()}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                        {!img.is_primary ? (
                          <Button size="small" variant="outlined" onClick={() => setPrimary(img.id)}>
                            Set Primary
                          </Button>
                        ) : (
                          <Typography sx={{ fontSize: '0.75rem', color: color.success[700], fontWeight: 600 }}>
                            ✓ Active Hero Photo
                          </Typography>
                        )}
                        <IconButton size="small" color="error" onClick={() => deleteImage(img.id)} aria-label="Delete image">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* TAB 1: SPECIFICATIONS & HIGHLIGHTS */}
      {tab === 1 && (
        <Stack spacing={2.5}>
          <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
            <b>Storefront Highlight Bullets:</b> Enter each key specification on a new line. These bullets are rendered directly on product showcase cards, shelf quick-specs, and mobile product pages.
          </Alert>

          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Key Specifications (One bullet per line) *"
            value={form.short_desc}
            onChange={e => set('short_desc', e.target.value)}
            placeholder={`6.1-inch Super Retina XDR display\nA15 Bionic chip with 6-core CPU\nDual 12MP camera system (Wide and Ultra Wide)\nCeramic Shield front glass\nUp to 19 hours video playback`}
            helperText="Tip: 4-6 concise bullets work best for shelf cards and mobile preview."
          />

          {specBullets.length > 0 && (
            <Box sx={{ p: 2, bgcolor: color.ink[50], borderRadius: `${radius.lg}px`, border: `1px solid ${color.border}` }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: color.ink[700], textTransform: 'uppercase', mb: 1 }}>
                Live Storefront Bullet Preview:
              </Typography>
              <Stack spacing={0.5}>
                {specBullets.map((b, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Typography sx={{ color: color.primary[600], fontWeight: 700 }}>•</Typography>
                    <Typography sx={{ fontSize: '0.825rem', color: color.ink[800] }}>{b}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          <Divider />

          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Detailed Overview & Rich Description"
            value={form.description}
            onChange={e => set('description', e.target.value)}
            placeholder="Comprehensive description of the product, features, what's in the box, and manufacturer details..."
          />
        </Stack>
      )}

      {/* TAB 2: BASIC INFO & PRICE */}
      {tab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Customer-Facing Product Title *"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              helperText="Clean, easy-to-read name shown to buyers on the website (e.g. Apple iPhone 12 (64 GB) Blue)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Brand"
              value={form.brand}
              onChange={e => set('brand', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={form.category_id}
                label="Category"
                onChange={e => set('category_id', e.target.value)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="MRP (₹)"
              value={form.mrp}
              onChange={e => set('mrp', parseFloat(e.target.value) || 0)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Sale / MOP Price (₹) *"
              value={form.sale_price}
              onChange={e => set('sale_price', parseFloat(e.target.value) || 0)}
              helperText="Customer purchase price on the storefront"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="ERP Item Code (Warehouse ID)"
              value={form.item_code}
              disabled
              helperText="Managed by FCA ERP master catalog"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Current Warehouse Stock Qty"
              value={form.stock_qty}
              disabled
              helperText="Synchronized from FCA store warehouse inventory"
              InputProps={{
                startAdornment: <InputAdornment position="start"><StockIcon fontSize="small" /></InputAdornment>
              }}
            />
          </Grid>
        </Grid>
      )}

      {/* TAB 3: MERCHANDISING & FLAGS */}
      {tab === 3 && (
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderRadius: `${radius.lg}px`,
              border: `1px solid ${form.is_active ? color.success[300] : color.border}`,
              bgcolor: form.is_active ? color.success[50] : color.surface,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: color.ink[900] }}>
                Storefront Visibility (Active Status)
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: color.ink[500] }}>
                When active, buyers can search, view, and purchase this product online.
              </Typography>
            </Box>
            <Switch
              color="success"
              checked={form.is_active === 1}
              onChange={e => set('is_active', e.target.checked ? 1 : 0)}
            />
          </Box>

          <Divider sx={{ my: 1 }} />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: color.ink[500], textTransform: 'uppercase' }}>
            Special Homepage & Showcase Placement Flags:
          </Typography>

          {MARKETING_FLAGS.map(f => (
            <Box
              key={f.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 2,
                py: 1.5,
                borderRadius: `${radius.lg}px`,
                border: `1px solid ${form[f.key] ? color.primary[300] : color.border}`,
                bgcolor: form[f.key] ? color.primary[50] : color.surface,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: color.ink[900] }}>
                  {f.label}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: color.ink[500] }}>
                  {f.description}
                </Typography>
              </Box>
              <Switch
                color="primary"
                checked={!!form[f.key]}
                onChange={e => set(f.key, e.target.checked ? 1 : 0)}
              />
            </Box>
          ))}
        </Stack>
      )}
    </FormDialog>
  );
};

const ProductsV2 = () => {
  const { success, error } = useFeedback();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingFca, setSyncingFca] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [isActive, setIsActive] = useState('');
  const [imageFilter, setImageFilter] = useState('');
  const [stockStatus, setStockStatus] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, total: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchStats = async () => {
    try {
      const res = await api.get('/api/v2/products/admin/stats');
      if (res.data?.success) {
        setStats(res.data.data);
      }
    } catch {}
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (category) params.category = category;
      if (brand) params.brand = brand;
      if (isActive !== '') params.is_active = isActive;
      if (imageFilter !== '') params.has_image = imageFilter;
      if (stockStatus !== '') params.stock_status = stockStatus;
      if (flagFilter !== '') params.flag = flagFilter;
      if (sort) params.sort = sort;

      const res = await api.get('/api/v2/products/admin/all', { params });
      setProducts(res.data.data || []);
      setPagination(res.data.pagination || { totalPages: 1, total: 0 });
    } catch (err) {
      error(err.response?.data?.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [page, search, category, brand, isActive, imageFilter, stockStatus, flagFilter, sort, error]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/api/v2/categories/flat');
      setCategories(res.data.data || []);
    } catch {}
  };

  const fetchBrands = async () => {
    try {
      const res = await api.get('/api/v2/products/brands');
      setBrands(res.data.data || []);
    } catch {}
  };

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleEdit = (p) => {
    setEditProduct(p);
    setDialogOpen(true);
  };

  const handleSyncFca = async () => {
    setSyncingFca(true);
    try {
      const res = await api.post('/api/v2/products/admin/sync-fca');
      success(res.data?.message || 'Catalog synchronized with FCA ERP');
      fetchProducts();
      fetchStats();
    } catch (err) {
      error(err.response?.data?.message || 'FCA synchronization failed');
    } finally {
      setSyncingFca(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/api/v2/products/${p.id}`, { is_active: p.is_active ? 0 : 1 });
      fetchProducts();
      fetchStats();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to update status');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/v2/products/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchProducts();
      fetchStats();
      success('Product deactivated');
    } catch (err) {
      error(err.response?.data?.message || 'Failed');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Product & ERP Master',
      width: 380,
      render: (_, row) => {
        const hasImg = Boolean(row.primary_image && row.image_count > 0);
        const hasSpecs = Boolean(row.short_desc && row.short_desc.trim().length > 5);
        return (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, py: 0.5 }}>
            <Avatar
              variant="rounded"
              src={getImageUrl(row.primary_image)}
              sx={{
                width: 48,
                height: 48,
                bgcolor: '#ffffff',
                border: hasImg ? `1px solid ${color.border}` : `1px dashed ${color.danger[400]}`,
                p: 0.5,
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                cursor: 'pointer'
              }}
              onClick={() => handleEdit(row)}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: color.ink[900],
                    maxWidth: 240,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    '&:hover': { color: color.primary[600] }
                  }}
                  onClick={() => handleEdit(row)}
                >
                  {row.name}
                </Typography>
                {!hasImg && (
                  <StatusBadge tone="danger" label="No Image" dot={false} />
                )}
                {!hasSpecs && (
                  <StatusBadge tone="neutral" label="No Specs" dot={false} />
                )}
              </Box>

              {row.erp_name && row.erp_name !== row.name && (
                <Typography sx={{ fontSize: '0.725rem', color: color.ink[500], fontStyle: 'italic', mb: 0.25 }}>
                  ERP: {row.erp_name}
                </Typography>
              )}

              <Stack direction="row" spacing={0.75} alignItems="center">
                <Chip
                  size="small"
                  label={row.item_code || row.sku || `#${row.id}`}
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    bgcolor: color.ink[100],
                    color: color.ink[700]
                  }}
                />
                <Typography sx={{ fontSize: '0.7rem', color: hasImg ? color.success[700] : color.danger[700], fontWeight: 600 }}>
                  {row.image_count > 0 ? `${row.image_count} photo(s)` : '0 photos'}
                </Typography>
              </Stack>
            </Box>
          </Box>
        );
      },
    },
    {
      key: 'category_name',
      label: 'Category & Brand',
      render: (_, row) => (
        <Box>
          <Typography sx={{ fontSize: '0.825rem', fontWeight: 600, color: color.ink[900] }}>
            {row.category_name || 'General'}
          </Typography>
          <Typography sx={{ fontSize: '0.725rem', color: color.ink[500] }}>
            {row.brand || 'Hotspot'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'sale_price',
      label: 'Price (₹)',
      align: 'right',
      render: (_, row) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: color.ink[900] }}>
            ₹{Number(row.sale_price).toLocaleString()}
          </Typography>
          {row.mrp > row.sale_price && (
            <Typography sx={{ fontSize: '0.725rem', color: color.ink[400], textDecoration: 'line-through' }}>
              ₹{Number(row.mrp).toLocaleString()}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'stock_qty',
      label: 'Stock',
      align: 'center',
      render: (v) => {
        const qty = Number(v) || 0;
        const tone = qty === 0 ? 'danger' : qty <= 5 ? 'warning' : 'success';
        return <StatusBadge tone={tone} label={qty === 0 ? 'Out of stock' : `${qty} in stock`} />;
      },
    },
    {
      key: 'is_featured',
      label: 'Flags',
      align: 'center',
      render: (_, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          {FLAG_BADGES.filter(f => row[f.key]).map(f => (
            <Tooltip key={f.key} title={f.label} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <StatusBadge
                  tone={f.tone}
                  dot={false}
                  label={f.letter}
                  sx={{ minWidth: 22, height: 20, justifyContent: 'center', px: 0.5, fontWeight: 700 }}
                />
              </Box>
            </Tooltip>
          ))}
          {!FLAG_BADGES.some(f => row[f.key]) && (
            <Typography sx={{ fontSize: '0.75rem', color: color.ink[300] }}>—</Typography>
          )}
        </Stack>
      ),
    },
    {
      key: 'is_active',
      label: 'Active',
      align: 'center',
      render: (_, row) => (
        <Switch size="small" color="success" checked={!!row.is_active} onChange={() => toggleActive(row)} />
      ),
    },
    {
      key: 'actions',
      label: 'Manage',
      align: 'center',
      render: (_, row) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<CameraIcon fontSize="small" />}
          onClick={() => handleEdit(row)}
          sx={{
            fontSize: '0.75rem',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: `${radius.md}px`,
            py: 0.5,
            px: 1.5,
            borderColor: color.border,
            color: color.ink[800],
            '&:hover': { borderColor: color.primary[500], bgcolor: color.primary[50], color: color.primary[700] }
          }}
        >
          Photos & Specs
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="ERP CATALOG MANAGEMENT"
        title="Products Catalog"
        subtitle={`${pagination.total.toLocaleString()} products synced from FCA ERP • Manage packshots, specs, and merchandising`}
        actions={
          <Button
            variant="contained"
            startIcon={syncingFca ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncFca}
            disabled={syncingFca}
            sx={{
              bgcolor: color.primary[600],
              '&:hover': { bgcolor: color.primary[700] },
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: `${radius.md}px`
            }}
          >
            {syncingFca ? 'Syncing with FCA ERP...' : 'Sync Catalog from FCA'}
          </Button>
        }
      />

      {/* Metric Quick Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: `${radius.lg}px`, borderColor: color.border }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography sx={{ fontSize: '0.75rem', color: color.ink[500], fontWeight: 600, textTransform: 'uppercase' }}>
                  Total Catalog Items
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: color.ink[900], mt: 0.5 }}>
                  {Number(stats.total_products || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: `${radius.lg}px`,
                borderColor: imageFilter === 'missing' ? color.danger[500] : color.border,
                bgcolor: imageFilter === 'missing' ? color.danger[50] : color.surface,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: color.danger[400] }
              }}
              onClick={() => {
                setImageFilter(imageFilter === 'missing' ? '' : 'missing');
                setPage(1);
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: '0.75rem', color: color.danger[700], fontWeight: 700, textTransform: 'uppercase' }}>
                    Needs Photos (Action)
                  </Typography>
                  <WarningIcon sx={{ fontSize: 18, color: color.danger[600] }} />
                </Stack>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: color.danger[700], mt: 0.5 }}>
                  {Number(stats.missing_images || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: `${radius.lg}px`,
                borderColor: imageFilter === 'has_image' ? color.success[500] : color.border,
                bgcolor: imageFilter === 'has_image' ? color.success[50] : color.surface,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: color.success[400] }
              }}
              onClick={() => {
                setImageFilter(imageFilter === 'has_image' ? '' : 'has_image');
                setPage(1);
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: '0.75rem', color: color.success[700], fontWeight: 700, textTransform: 'uppercase' }}>
                    Storefront Ready (Imaged)
                  </Typography>
                  <CheckCircleIcon sx={{ fontSize: 18, color: color.success[600] }} />
                </Stack>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: color.success[700], mt: 0.5 }}>
                  {Number(stats.has_images || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: `${radius.lg}px`,
                borderColor: stockStatus === 'in_stock' ? color.primary[500] : color.border,
                bgcolor: stockStatus === 'in_stock' ? color.primary[50] : color.surface,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: color.primary[400] }
              }}
              onClick={() => {
                setStockStatus(stockStatus === 'in_stock' ? '' : 'in_stock');
                setPage(1);
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: '0.75rem', color: color.ink[700], fontWeight: 700, textTransform: 'uppercase' }}>
                    In-Stock Inventory
                  </Typography>
                  <StockIcon sx={{ fontSize: 18, color: color.primary[600] }} />
                </Stack>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: color.ink[900], mt: 0.5 }}>
                  {Number(stats.in_stock || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter Toolbar */}
      <Panel dense sx={{ mb: 2 }}>
        <Toolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Search by Name, ERP ItemCode, Brand, or SKU..."
          onRefresh={() => { fetchProducts(); fetchStats(); }}
          refreshing={loading}
        >
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={e => { setCategory(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Brand</InputLabel>
              <Select
                value={brand}
                label="Brand"
                onChange={e => { setBrand(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All Brands</MenuItem>
                {brands.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Images Status</InputLabel>
              <Select
                value={imageFilter}
                label="Images Status"
                onChange={e => { setImageFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All Images</MenuItem>
                <MenuItem value="missing">⚠️ Needs Photos (Missing)</MenuItem>
                <MenuItem value="has_image">✅ Store Ready (Has Photo)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Stock</InputLabel>
              <Select
                value={stockStatus}
                label="Stock"
                onChange={e => { setStockStatus(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All Stock</MenuItem>
                <MenuItem value="in_stock">In Stock</MenuItem>
                <MenuItem value="out_of_stock">Out of Stock</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={isActive}
                label="Status"
                onChange={e => { setIsActive(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Active (Online)</MenuItem>
                <MenuItem value="0">Hidden (Offline)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Homepage Flags</InputLabel>
              <Select
                value={flagFilter}
                label="Homepage Flags"
                onChange={e => { setFlagFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All Flags</MenuItem>
                <MenuItem value="featured">Featured [F]</MenuItem>
                <MenuItem value="todays_deal">Today's Deal [D]</MenuItem>
                <MenuItem value="best_seller">Best Seller [B]</MenuItem>
                <MenuItem value="new_release">New Release [N]</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sort}
                label="Sort By"
                onChange={e => { setSort(e.target.value); setPage(1); }}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="missing-images">Needs Photos First</MenuItem>
                <MenuItem value="has-images">Has Photos First</MenuItem>
                <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                <MenuItem value="price-desc">Price (High to Low)</MenuItem>
                <MenuItem value="price-asc">Price (Low to High)</MenuItem>
                <MenuItem value="stock-desc">Stock (High to Low)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Toolbar>
      </Panel>

      {/* Main Table */}
      <DataTable
        columns={columns}
        rows={products}
        rowKey="id"
        loading={loading}
        minWidth={1050}
        emptyTitle="No products match your filters"
        emptyDescription="Try clearing your search or filters to see more catalog products."
      />

      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5, mb: 1 }}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Deactivate confirmation dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Deactivate product"
        description={deleteTarget ? `Hide "${deleteTarget.name}" from the online storefront?` : ''}
        confirmLabel="Deactivate"
        tone="danger"
        loading={deleting}
      />

      {/* Edit drawer / modal */}
      <ProductEditDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        product={editProduct}
        categories={categories}
        onSaved={() => {
          setDialogOpen(false);
          fetchProducts();
          fetchStats();
        }}
      />
    </Box>
  );
};

export default ProductsV2;
